/* eslint-disable react/style-prop-object */
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'

import Checkbox from 'components/Checkbox'
import CWLoader from 'components/CWLoader/CWLoader'
import Drawer from 'components/Drawer'
import Hint from 'components/Hint'
import SelectDatePicker from 'components/TimeSelect/SelectDatePicker'
import Toggle from 'components/Toggle'
import Button from 'ui/Button'
import Input from 'ui/Input'
import InputLabel from 'ui/InputLabel'
import Separator from 'ui/Separator'
import { buildFormError } from 'utils/functions/forms'

import { useMetricsSubjectQuery } from 'utils/hooks/useMetricsSubjectQuery'
import { ErrorType } from 'utils/types/common'
import { Form, Legend, LoaderWrapper } from '../EditMetric/EditMetric.styles'
import {
  ButtonsContainer,
  CheckboxContainer,
} from './SetMetricMilestone.styles'
import useSetMetricMilestone from './useSetMetricMilestone'

const SetMetricMilestone = ({ closeDrawer, isEditingMilestone, isOpen }) => {
  const {
    formik,
    onDeleteMilestone,
    metric,
    milestone,
    isLoadingMetric,
    isSaving,
    isFounder,
    isSystemMetric,
  } = useSetMetricMilestone(closeDrawer, isEditingMilestone)
  const { data: subject } = useMetricsSubjectQuery({
    subjectId: metric?.subject.id!,
    subjectType: metric?.subject.type!,
  })

  const intl = useIntl()
  const history = useHistory()

  if (isLoadingMetric) {
    return (
      <LoaderWrapper>
        <CWLoader />
      </LoaderWrapper>
    )
  }

  return (
    <Drawer
      isOpen={isOpen}
      onCloseDrawer={() => history.goBack()}
      headerTitle={
        isEditingMilestone
          ? intl.formatMessage({ id: 'metrics.editGroupMilestone' })
          : intl.formatMessage({ id: 'metrics.setGroupMilestone' })
      }
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault()
          formik.submitForm()
        }}
      >
        <Drawer.Content>
          <Legend>
            {intl.formatMessage({ id: 'metrics.milestoneLegend' })}
          </Legend>
          <Input
            type="number"
            name="value"
            error={buildFormError(
              formik.errors.value,
              ErrorType.ERROR,
              formik.touched.value
            )}
            value={formik.values.value}
            onChange={formik.handleChange}
            label={intl.formatMessage({ id: 'metrics.milestone' })}
            placeholder={intl.formatMessage({ id: 'metrics.addValue' })}
            onBlur={formik.handleBlur}
          />

          <Separator space="3rem" />

          <SelectDatePicker
            id="date"
            name="date"
            handleChange={(date) => formik.setFieldValue('date', date)}
            placeholder={intl.formatMessage({ id: 'metrics.selectDate' })}
            label={intl.formatMessage({ id: 'metrics.whenShouldItBeReached' })}
            onlyMonthYearPicker={false}
            error={buildFormError(
              formik.errors.date,
              ErrorType.ERROR,
              formik.touched.date
            )}
            dateFormat="dd MMM, yyyy"
            maxDate={null}
            minDate={new Date()}
            value={milestone?.date ?? undefined}
            onBlur={() => {
              formik.setFieldTouched('date', true)
            }}
          />
          <Separator space="1rem" />

          {!isFounder && !isSystemMetric && (
            <Toggle
              id="shared"
              name="shared"
              status={formik.values.shared}
              onChangeStatus={(status) =>
                formik.setFieldValue('shared', status)
              }
              blue
              legend={intl.formatMessage(
                { id: 'metrics.shareMilestoneWith' },
                { company: subject?.name }
              )}
            />
          )}

          <Separator space="1.5rem" />

          <InputLabel>
            {intl.formatMessage({ id: 'metrics.alertMeWhen' })}
          </InputLabel>

          <CheckboxContainer>
            <Checkbox
              id="notifyValueReached"
              name="notifyValueReached"
              label={intl.formatMessage({ id: 'metrics.hitAlert' })}
              checked={formik.values.notifyValueReached}
              onChange={formik.handleChange}
            />
            <Separator space="0.8rem" />
            <Checkbox
              id="notifyPercentageReached"
              name="notifyPercentageReached"
              label={intl.formatMessage({ id: 'metrics.percentAlert' })}
              checked={formik.values.notifyPercentageReached}
              onChange={formik.handleChange}
            />
          </CheckboxContainer>

          <Separator space="2rem" />

          {isFounder && (
            <Hint>
              <FormattedMessage id="metrics.milestonesOnlySeenByGroup" />
            </Hint>
          )}
        </Drawer.Content>

        <Drawer.Footer>
          <ButtonsContainer isEditingMilestone={isEditingMilestone}>
            {isEditingMilestone && (
              <Button type="button" danger onClick={onDeleteMilestone}>
                <FormattedMessage id="general.delete" />
              </Button>
            )}

            <ButtonsContainer>
              <Button secondary type="button" onClick={history.goBack}>
                <FormattedMessage id="general.cancel" />
              </Button>
              <Button type="submit" primary isDisabled={isSaving}>
                <FormattedMessage id="general.save" />
              </Button>
            </ButtonsContainer>
          </ButtonsContainer>
        </Drawer.Footer>
      </Form>
    </Drawer>
  )
}

SetMetricMilestone.propTypes = {
  closeDrawer: PropTypes.func.isRequired,
  isEditingMilestone: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
}

SetMetricMilestone.defaultProps = {
  isEditingMilestone: false,
}

export default SetMetricMilestone
